/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { useRef } from 'preact/hooks';
import { cancelEvent } from '#utils/UNSAFE_visUtils';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { useTimeComponentKeyboard } from '#PRIVATE_TimeComponent/hooks/useTimeComponentKeyboard';
import { useSelection } from '#hooks/PRIVATE_useVisSelection';
import type {
  GanttCurrentRowLabel,
  GanttCurrentTask,
  GanttRowData,
  GanttSelection as GanttSelectionProps,
  GanttTaskData
} from '../gantt.types';
import type { getRowDataLayout } from '../utils/dataLayoutUtils';
import {
  getDefaultInitialFocusTask,
  getRowLabelNavigableFromTask,
  getTaskNavigableFromRowLabel
} from '../utils/navigationUtils';
import { useGanttTaskNavigation } from './useGanttTaskNavigation';
import { useGanttRowLabelNavigation } from './useGanttRowLabelNavigation';

/**
 * Hook that handles keyboard gestures on a Gantt.
 */
type UseGanttKeyboardOptions<
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
> = {
  visRef: RefObject<HTMLDivElement>;
  rowAxisRef: RefObject<HTMLDivElement>;
  rowDataLayout: ReturnType<typeof getRowDataLayout<K1, K2, D1, D2>>;
  navigationModes: {
    tasks: {
      currentItem: GanttCurrentTask<K2>['currentTask'];
      onCurrentItemChange: GanttCurrentTask<K2>['onCurrentTaskChange'];
    };
    rowLabels: {
      currentItem: GanttCurrentRowLabel<K1>['currentRowLabel'];
      onCurrentItemChange: GanttCurrentRowLabel<K1>['onCurrentRowLabelChange'];
    };
  };
  selectionProps: GanttSelectionProps<K2, D2>;
  isRTL: boolean;
  isDisabled?: boolean;
};

const useGanttKeyboard = <
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>({
  visRef,
  rowAxisRef,
  rowDataLayout,
  navigationModes,
  selectionProps,
  isRTL,
  isDisabled = false
}: UseGanttKeyboardOptions<K1, K2, D1, D2>) => {
  const currentNavMode = useRef<keyof typeof navigationModes>('tasks');

  const idExtracter = (event: Event) => {
    if (currentNavMode.current !== 'tasks' || event.type !== 'keyup') return;
    return navigationModes.tasks.currentItem?.id;
  };

  const { onKeyUp: selectionKeyup } = useSelection<K2, D2>({
    idExtracter,
    selection: selectionProps.selectedIds,
    selectionMode: selectionProps.selectionMode,
    onChange: selectionProps.onSelectionChange,
    // TODO: rowDataLayout is undefined when isDisabled is true. Let's reevalute the lifecycle when we work on JET-62889
    getDataById: rowDataLayout?.getTaskDataById || (() => undefined)
  }).selectionProps;
  const selectionKeyboardProps =
    currentNavMode.current === 'tasks' ? { onKeyUp: selectionKeyup } : {};

  const keyboardProps = useTimeComponentKeyboard({
    visRef,
    isDisabled
  });

  const taskNavProps = useGanttTaskNavigation({
    rowDataLayout,
    currentTask: navigationModes.tasks.currentItem,
    onCurrentTaskChange: navigationModes.tasks.onCurrentItemChange,
    isRTL
  });

  const rowLabelNavProps = useGanttRowLabelNavigation({
    rowDataLayout,
    currentRowLabel: navigationModes.rowLabels.currentItem,
    onCurrentRowLabelChange: navigationModes.rowLabels.onCurrentItemChange
  });

  const onKeyDown = (event: KeyboardEvent) => {
    let stopImmediatePropagation = false;
    const applyFocus = (item: { id: K1; type: 'rowLabels' } | { id: K2; type: 'tasks' }) => {
      const { onCurrentItemChange } = navigationModes[item.type];
      onCurrentItemChange({ id: item.id as any, isFocused: true });
    };
    const removeFocus = (navType: keyof typeof navigationModes) => {
      const { currentItem, onCurrentItemChange } = navigationModes[navType];
      currentItem && onCurrentItemChange({ id: currentItem.id as any, isFocused: false });
    };
    const enterRowLabelsNavigation = () => {
      const id = getRowLabelNavigableFromTask(navigationModes.tasks.currentItem!.id, rowDataLayout);
      applyFocus({ id, type: 'rowLabels' });
      removeFocus('tasks');
      currentNavMode.current = 'rowLabels';
      stopImmediatePropagation = true;
      cancelEvent(event);
    };
    const enterTasksNavigation = () => {
      const id = getTaskNavigableFromRowLabel(
        navigationModes.rowLabels.currentItem!.id,
        rowDataLayout,
        navigationModes.tasks.currentItem?.id
      );
      id !== undefined && applyFocus({ id, type: 'tasks' });
      removeFocus('rowLabels');
      currentNavMode.current = 'tasks';
      stopImmediatePropagation = true;
      cancelEvent(event);
    };

    const key = event.key;
    switch (key) {
      case 'Tab': {
        // Tab OUT of component
        // Remove focus visuals
        removeFocus(currentNavMode.current);
        break;
      }
      case 'ArrowLeft': {
        if (event.altKey) {
          if (!isRTL && currentNavMode.current === 'tasks') {
            enterRowLabelsNavigation();
          } else if (isRTL && currentNavMode.current === 'rowLabels') {
            enterTasksNavigation();
          }
        }
        break;
      }
      case 'ArrowRight': {
        if (event.altKey) {
          if (!isRTL && currentNavMode.current === 'rowLabels') {
            enterTasksNavigation();
          } else if (isRTL && currentNavMode.current === 'tasks') {
            enterRowLabelsNavigation();
          }
        }
        break;
      }
    }

    if (!stopImmediatePropagation) {
      switch (currentNavMode.current) {
        case 'tasks':
          taskNavProps.onKeyDown?.(event);
          break;
        case 'rowLabels':
          rowLabelNavProps.onKeyDown?.(event);
          break;
        default: {
          const _exhaustiveCheck: never = currentNavMode.current;
          return _exhaustiveCheck;
        }
      }
    }
  };

  const onKeyUp = (event: KeyboardEvent) => {
    const key = event.key;
    switch (key) {
      case 'Tab': {
        // Tab INTO component
        const { currentItem, onCurrentItemChange } = navigationModes[currentNavMode.current];
        // currentItem should only ever be undefined on first Tab
        const itemToFocus = currentItem ? currentItem : getDefaultInitialFocusTask(rowDataLayout);
        itemToFocus && onCurrentItemChange({ id: itemToFocus.id as any, isFocused: true });
        cancelEvent(event);
        break;
      }
    }
  };

  // TODO: JET-62887: Avoid this temporary hack to get keyboard datatip to work (until we find a way to get rid of all these dispatchEvents)
  const focusDatatipProps = {
    onFocus: () => {
      const e = new Event('ShowFocusDatatip');
      visRef.current?.dispatchEvent(e);
      rowAxisRef.current?.dispatchEvent(e);
    }
  };

  return isDisabled
    ? {}
    : mergeProps(keyboardProps, focusDatatipProps, { onKeyDown, onKeyUp }, selectionKeyboardProps);
};

export { useGanttKeyboard };
